import { Link } from "react-router-dom";
import { useState } from "react";
import { Mail, MapPin, Phone, Facebook } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO = "/logo.png";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      toast.success("Subscribed - thank you!");
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer className="bg-white mt-auto" data-testid="site-footer">
      {/* Newsletter band - warm cream */}
      <section className="bg-[var(--cream)] border-y border-[var(--line-warm)]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-14 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <p className="eyebrow mb-3">★ Stay Connected</p>
            <h3 className="font-display text-3xl md:text-4xl text-[var(--blue-ink)] leading-tight">
              Subscribe to the TIoB Weekly
            </h3>
            <p className="mt-3 text-[var(--ink-soft)]">
              Our free weekly newsletter with service times, event invitations, and
              community news &mdash; open to everyone.
            </p>
            <p className="mt-2 text-sm text-[var(--muted)]">
              Members also receive the bi-monthly <em>TIOB Bulletin</em> with in-depth
              lifecycle announcements, yahrzeits, and board updates.
            </p>
          </div>
          <form onSubmit={subscribe} className="flex flex-col sm:flex-row gap-3" data-testid="newsletter-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="flex-1 px-5 py-4 rounded-full bg-white border border-[var(--line)] focus:outline-none focus:border-[var(--blue)] text-[var(--ink)]"
              aria-label="Email address"
              data-testid="newsletter-email-input"
            />
            <button
              type="submit"
              disabled={busy}
              className="btn-primary justify-center disabled:opacity-60"
              data-testid="newsletter-submit-btn"
            >
              {busy ? "Subscribing…" : "Subscribe"}
            </button>
          </form>
        </div>
      </section>

      {/* Main footer - light */}
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 grid md:grid-cols-2 lg:grid-cols-4 gap-10 text-[var(--ink-soft)]">
        <div>
          <img src={LOGO} alt="Temple Israel of Brevard" className="h-14 w-auto mb-5" />
          <p className="text-sm leading-relaxed">
            A welcoming Reform Jewish congregation serving Brevard County, Florida since 1965.
            Proud member of the Union for Reform Judaism.
          </p>
          <a
            href="https://www.facebook.com/TempleIsraelBrevardFL"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-5 text-[var(--blue-deep)] hover:text-[var(--blue)]"
            data-testid="facebook-link"
          >
            <Facebook size={18} /> <span className="text-sm font-medium">Follow on Facebook</span>
          </a>
        </div>

        <div>
          <h4 className="eyebrow mb-5">Explore</h4>
          <ul className="space-y-3 text-sm">
            <li><Link className="hover:text-[var(--blue-deep)]" to="/about" data-testid="footer-about">About</Link></li>
            <li><Link className="hover:text-[var(--blue-deep)]" to="/rabbi">Meet Our Rabbi</Link></li>
            <li><Link className="hover:text-[var(--blue-deep)]" to="/worship">Worship & Services</Link></li>
            <li><Link className="hover:text-[var(--blue-deep)]" to="/education">Education</Link></li>
            <li><Link className="hover:text-[var(--blue-deep)]" to="/events">Events Calendar</Link></li>
            <li><Link className="hover:text-[var(--blue-deep)]" to="/gallery">Photo Gallery</Link></li>
            <li><Link className="hover:text-[var(--blue-deep)]" to="/bulletin">Temple Bulletin</Link></li>
            <li><Link className="hover:text-[var(--blue-deep)]" to="/membership">Membership</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="eyebrow mb-5">Visit & Contact</h4>
          <ul className="space-y-3 text-sm">
            <li className="flex gap-3"><MapPin size={16} className="mt-0.5 shrink-0 text-[var(--blue)]" />
              <span>
                <strong className="text-[var(--ink)]">Visit:</strong> 7300 Lake Andrew Drive<br />Melbourne, FL 32940
              </span>
            </li>
            <li className="flex gap-3"><MapPin size={16} className="mt-0.5 shrink-0 text-[var(--blue)]" />
              <span>
                <strong className="text-[var(--ink)]">Mail:</strong> 2328 Citadel Way,<br />
                Ste. 103, Box 222<br />Melbourne, FL 32940
              </span>
            </li>
            <li className="flex gap-3"><Phone size={16} className="mt-0.5 shrink-0 text-[var(--blue)]" />
              <a href="tel:+13216319494" className="hover:text-[var(--blue-deep)]">(321) 631-9494</a>
            </li>
            <li className="flex gap-3"><Mail size={16} className="mt-0.5 shrink-0 text-[var(--blue)]" />
              <a href="mailto:tioffice@tiofbrevard.com" className="hover:text-[var(--blue-deep)] break-all">tioffice@tiofbrevard.com</a>
            </li>
            <li className="text-[var(--muted)] text-xs uppercase tracking-widest mt-4 font-mono-accent">Office Hours</li>
            <li>Tue&ndash;Fri 9 AM &ndash; 2 PM <span className="text-[var(--muted)]">(by appt)</span></li>
          </ul>
        </div>

        <div>
          <h4 className="eyebrow mb-5">Support</h4>
          <ul className="space-y-3 text-sm">
            <li><Link className="hover:text-[var(--blue-deep)]" to="/donate">Donate</Link></li>
            <li><Link className="hover:text-[var(--blue-deep)]" to="/membership">Become a Member</Link></li>
            <li><Link className="hover:text-[var(--blue-deep)]" to="/contact">Contact</Link></li>
            <li><Link className="hover:text-[var(--blue-deep)]" to="/accessibility" data-testid="footer-accessibility-link">Accessibility Statement</Link></li>
            <li><Link className="hover:text-[var(--blue-deep)]" to="/privacy">Privacy Policy</Link></li>
            <li><Link className="hover:text-[var(--blue-deep)]" to="/terms">Terms & Conditions</Link></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-[var(--line)] bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-6 flex flex-col md:flex-row gap-3 items-center justify-between text-xs text-[var(--muted)]">
          <p>© {new Date().getFullYear()} Temple Israel of Brevard. All rights reserved.</p>
          <p data-testid="brand-partners-credit">
            Website Designed By{" "}
            <a
              href="https://bp.agency"
              target="_blank"
              rel="noopener noreferrer"
              className="font-bold text-[var(--blue-ink)] hover:text-[var(--blue)]"
              data-testid="brand-partners-link"
            >
              Brand Partners
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
